import { Directive, HostListener, Input, OnChanges, OnInit } from '@angular/core';
import { InputBaseComponent } from '../../input-base/input-base.component';
import { removeFormControlError } from '../../../misc/form.helpers';

@Directive({
  // tslint:disable-next-line:directive-selector
  selector: '[inputNumber]',
  standalone: false
})
export class InputNumberDirective implements OnInit, OnChanges {

  @Input() public allowNegative: boolean = true;                 // determines if '-' is allowed. Prevents input of -
  @Input() public minTotal: number = 0;                          // minimal total value including decimal, null sets no restrictions - if out of bounds an error is attached to the input
  @Input() public maxTotal: number | null = null;                // max total value including decimals, null sets no restrictions - if out of bounds an error is attached to the input
  @Input() public maxDecimalPlaces: number = 2;                  // max allowed decimals - prevents adding more than specified
  @Input() public addDecimalZerosPadding: boolean = false;       // if decimals present -> add zeros to match max allowed decimals
  @Input() public forceDecimals: boolean = false;                // true -> 1 gets transformed to 1.00 (0-count according to maxDecimalPlaces)
  @Input() public formatOnInit: boolean = false;                 // true -> format control initially in NgOnInit

  @Input() public errorTextMinUndershot: string = 'Value must be at least {{val}}';
  @Input() public errorTextMaxExceeded: string = 'The maximum value is {{val}}';

  constructor(private parent: InputBaseComponent) {
  }


  public ngOnChanges(): void {
    if (this.formatOnInit) {
      this.ngOnInit();
    }
  }

  public ngOnInit(): void {
    if (this.formatOnInit) {
      this.whenLeaving();
    }
  }

  @HostListener('click', ['$event'])
  public selectWhenZero(event: any): void {
    if (+this.parent.control.value === 0 && typeof event.target.select !== 'undefined') {
      event.target.select();
    }
  }

  // instant reaction on keypress
  @HostListener('input', ['$event'])
  public onInput(event: Event): void {
    this.transformValue((event.target as HTMLInputElement).value);
  }

  private transformValue(value: string | number): void {
    if (this.parent.control) {
      value = '' + value;
      if (value.length > 11) {
        value = value.substr(0, 12);
      }
      if (!this.allowNegative && value.includes('-')) {
        this.parent.control.setValue(value.replace('-', ''));
        return;
      }
      if (value === '-0') {
        this.parent.control.setValue('0');
        return;
      }

      let amount = ('' + value).replace(',', '.');
      if (!this.maxDecimalPlaces) {
        amount = amount.replace('.', '');
      }

      const beforePoint = amount.split('.')[0];
      let integers = '';
      if (typeof beforePoint !== 'undefined' && beforePoint !== '') {
        integers = beforePoint.replace(/-?\D+/g, '');
        if (integers !== '') {
          integers = '' + parseInt(integers, 10);
        }
      }

      if (value && value.toString().startsWith('-')) {
        integers = '-' + integers;
      }

      const afterPoint = amount.split('.')[1];
      let decimals = '';
      if (typeof afterPoint !== 'undefined') {
        decimals = afterPoint.replace(/\D+/g, '');
      }


      if (decimals.length > this.maxDecimalPlaces) {
        decimals = decimals.slice(0, this.maxDecimalPlaces);
      }
      amount = integers;
      if (typeof afterPoint === 'string') {
        amount += '.';
      }
      if (decimals.length > 0) {
        amount += decimals;
      }

      this.parent.control.setValue(amount ?? null);
    }
  }

  // checking errors after leaving input or pressing enter
  @HostListener('focusout')
  @HostListener('keyup.enter')
  public whenLeaving(): void {
    if (!this.parent.control?.value) {
      return;
    }

    let val = '' + this.parent.control.value;
    if (!this.allowNegative) {
      val = val.replace('-', '');
    }

    const splitAtPoint = val.split('.');
    if (this.addDecimalZerosPadding && this.maxDecimalPlaces) {
      val = val + '0'.repeat(this.maxDecimalPlaces - splitAtPoint[1]?.length);
    }
    if (this.forceDecimals && !val.includes('.')) {
      val = val + '.' + '0'.repeat(this.maxDecimalPlaces);
    }
    if (val.endsWith('.')) {
      val = val.replace('.', '');
    }
    if (this.parent.control.value !== val) {
      this.parent.control.setValue(val);
    }
    if (this.minTotal !== null && this.minTotal > +val) {
      this.parent.control.setErrors({ minUndershot: this.errorTextMinUndershot.replace('{{val}}', this.minTotal.toString()) });
    }
    else {
      removeFormControlError(this.parent.control, 'minUndershot');
    }

    if (this.maxTotal !== null && this.maxTotal < +val) {
      this.parent.control.setErrors({ maxExceeded: this.errorTextMaxExceeded.replace('{{val}}', this.maxTotal.toString()) });
    }
    else {
      removeFormControlError(this.parent.control, 'maxExceeded');
    }
  }

}
